import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@vnetwork/ui/primitives';
import { SelectField } from '../../form-controls';
import { CommitmentFields } from '../commitment-fields';
import { PriceRangesList } from '../price-ranges-list';
import { TemplateSection, TemplateStack } from '../template-shell';
import type { ContractFormValues } from '../../contract-form';

const PRICE_METHOD_OPTIONS = [
  { value: 'tierd', label: 'Tiered' },
  { value: 'progressive', label: 'Progressive' },
];

/**
 * Per-region bandwidth config card within the CDN template. Renders commitment
 * fields, price-comparison method, and the tiered price-range list for one
 * `service_configoptions[i]` entry. Each region carries type `bandwidth` or
 * `base-config` (addon product variant) — both accept the same fields.
 */
export function CdnRegionConfig({
  optPath,
  regionName,
  totalRegions,
  onRemove,
}: {
  optPath: string;
  regionName: string;
  totalRegions: number;
  onRemove: () => void;
}) {
  const { control } = useFormContext<ContractFormValues>();
  const commitmentValue = Number(
    useWatch({ control, name: `${optPath}.commitment_value` as never }) ?? 0,
  );

  // Display "Total Bandwidth" when there is only one global region (legacy label).
  const displayName =
    regionName === 'global' && totalRegions === 1 ? 'Total Bandwidth' : regionName.toUpperCase();

  // Non-`local` regions may be removed (legacy: local region is permanent).
  const canRemove = regionName !== 'local' && totalRegions > 1;

  return (
    <TemplateSection
      title={displayName}
      action={
        canRemove ? (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} style={{ color: 'var(--vn-danger-text)' }}>
            Remove
          </Button>
        ) : undefined
      }
    >
      <TemplateStack>
        <CommitmentFields optPath={optPath} />
        <SelectField
          name={`${optPath}.price_config.method` as never}
          label="Form of comparison"
          options={PRICE_METHOD_OPTIONS}
          required
        />
        <PriceRangesList
          name={`${optPath}.prices`}
          commitmentValue={commitmentValue}
        />
      </TemplateStack>
    </TemplateSection>
  );
}
